/**
 * Resolves a human-readable performer display for a care-time
 * Observation when the saved `performer.display` is missing.
 *
 * Order of preference:
 *  1. The Observation's own `performer[0].display` (always preferred).
 *  2. The current Practitioner profile's resolved name via
 *     `getPractitionerDisplayName`.
 *  3. The literal `"Practitioner"` string as a last-resort fallback.
 *
 * Never returns the raw Practitioner UUID — the previous fallback to
 * `profile?.id` was the bug fix landed in audit item 1.4.
 * @agentic connections
 */

import type { Observation, Practitioner } from "@medplum/fhirtypes";
import { getPractitionerDisplayName } from "@benchmark/core/resolvers";

/**
 * Pick the best performer display string for a care-time Observation.
 * @agentic connections
 */
export const resolvePerformerDisplay = (
  observation: Observation,
  profile: Practitioner | undefined,
): string =>
  observation.performer?.[0]?.display ??
  (profile ? getPractitionerDisplayName(profile) : "Practitioner");
